import { useMemo, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { ActiveIngredientCalculatedQuantity } from '@dreryk/egabinet-prescription-frontend-api';

import { createTranslatedText, TranslatableText } from '@dreryk/react-common';

import {
   createActiveIngredientQuantityFormatter,
   getActiveIngredientCalculatedQuantites,
   MedicinePrescription,
} from '~libs.shared.prescription';
import { isEmpty } from '~libs.typescript';

import { prescriptionTranslationKeys } from '../../../../../translations';
import { PrescriptionTextValue } from '../PrescriptionTextValue';
import { Quantity } from '../quantity/quantity.types';

import { StyledActiveIngredientQuantity } from './StyledActiveIngredientQuantity';

type ActiveIngredientsQuantitiesTextValueProps = {
   readonly medicinePrescription: MedicinePrescription;
   readonly quantity: Quantity | null;
};

// eslint-disable-next-line max-lines-per-function
export const ActiveIngredientsQuantitiesTextValue = (props: ActiveIngredientsQuantitiesTextValueProps): JSX.Element => {
   const [activeIngredientCalculatedQuantities, setActiveIngredientCalculatedQuantities] = useState<
      ActiveIngredientCalculatedQuantity[] | null
   >(() => getActiveIngredientCalculatedQuantites(props.medicinePrescription, props.quantity));

   const value = useMemo<TranslatableText | undefined>(() => {
      if (isEmpty(activeIngredientCalculatedQuantities)) {
         return undefined;
      }

      return createTranslatedText(
         activeIngredientCalculatedQuantities.map(createActiveIngredientQuantityFormatter(false)).join(' + ')
      );
   }, [activeIngredientCalculatedQuantities]);

   const tooltipContent = useMemo<JSX.Element | undefined>(() => {
      if (isEmpty(activeIngredientCalculatedQuantities)) {
         return undefined;
      }

      return (
         <div>
            {activeIngredientCalculatedQuantities.map(createActiveIngredientQuantityFormatter(true)).map((quantity) => (
               <StyledActiveIngredientQuantity key={quantity}>{quantity}</StyledActiveIngredientQuantity>
            ))}
         </div>
      );
   }, [activeIngredientCalculatedQuantities]);

   useUpdateEffect(() => {
      const newActiveIngredientCalculatedQuantities = getActiveIngredientCalculatedQuantites(
         props.medicinePrescription,
         props.quantity
      );
      setActiveIngredientCalculatedQuantities(newActiveIngredientCalculatedQuantities);
   }, [props.quantity]);

   return (
      <PrescriptionTextValue
         testId={'activeIngredients'}
         label={prescriptionTranslationKeys.quantityOfActiveIngredients}
         value={value}
         tooltipContent={tooltipContent}
      />
   );
};
